<script lang="ts" setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { marked } from 'marked';
import { useAppStore } from '@/home/stores/app';
import { useAsyncData } from '@/home/hooks/async-data';
import { $fetch } from '@/home/plugins/fetch';
import { statsPv } from '@/home/utils';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },

  pageType: {
    type: String,
    default: 'detail',
  },
});

const router = useRouter();
const appStore = useAppStore();

const { data: detail } = useAsyncData<Record<string, any>>(
  'journalism:detail',
  () => $fetch('/news/v1/info', { data: { id: props.id } }),
  {
    cache: false,
    watch: [() => props.id],
    onAfterHandle({ handleSuccess, serverContext }) {
      if (serverContext) {
        if (handleSuccess && detail.value) {
          serverContext.title = detail.value.cur.title;
          serverContext.description = detail.value.cur.intro;
        }
        else {
          // 如果请求出错直接服务器重定向到 404 页面
          serverContext.redirect = '/404';
        }
      }
      else {
        if (handleSuccess && detail.value) {
          appStore.setTitle(detail.value?.cur.title);

          // 统计埋点: 因标题是异步获取的，所以 pv 要延迟统计
          statsPv();
        }
        else {
          router.replace('/404');
        }
      }
    },
  },
);

const markedContent = computed(() => {
  return detail.value?.cur.content ? marked.parse(detail.value.cur.content) : '';
});
</script>

<template>
  <main class="page-journalism-detail relative">
    <img class="news-top-image absolute left-0 top-0 -z-1" src="@/home/assets/journalism/circle.svg">

    <div class="page-journalism-detail__container mx-auto">
      <template v-if="detail">
        <div class="crumbs">
          <ul class="m-0 list-none p-0">
            <li class="crumbs-content">
              <RouterLink class="crumbs-list no-underline" to="/">
                <span class="crumbs-list-title">首页</span>
              </RouterLink>
              <template v-if="props.pageType === 'detail'">
                <span class="mx-8px">/</span>
                <RouterLink class="crumbs-list no-underline" to="/journalism">
                  <span class="crumbs-list-title">新闻动态</span>
                </RouterLink>
              </template>
              <span class="mx-8px">/</span>
              <span class="crumbs-list-title">{{ detail.cur.title }}</span>
            </li>
          </ul>
        </div>

        <div class="news-header">
          <h3 class="text-balance">{{ detail.cur.title }}</h3>
          <p>{{ detail.cur.releaseTime }}</p>
        </div>

        <!-- eslint-disable-next-line vue/no-v-html -->
        <div class="news-details-content" v-html="markedContent"></div>
      </template>
    </div>

    <div class="news-details-footed" v-if="props.pageType === 'detail' && detail">
      <div class="news-details-footed-list">
        <div class="news-details-footed-list__item">
          <h3 :title="detail.pre.title">
            <RouterLink :to="`/journalism/detail/${detail.pre.id}`" class="no-underline">
              {{ detail.pre.title }}
            </RouterLink>
          </h3>
          <small>{{ detail.pre.releaseTime }}</small>
          <p class="news-details-footed-list-content">
            {{ detail.pre.intro }}
          </p>
          <RouterLink :to="`/journalism/detail/${detail.pre.id}`" class="btn no-underline">
            <template v-if="detail.havePre">
              <span class="i-home-icons:arrow-left"></span>上一篇
            </template>
            <template v-else>
              推荐阅读
            </template>
          </RouterLink>
        </div>
        <div class="news-details-footed-list__item">
          <h3 :title="detail.next.title">
            <RouterLink :to="`/journalism/detail/${detail.next.id}`" class="no-underline">
              {{ detail.next.title }}
            </RouterLink>
          </h3>
          <small>{{ detail.next.releaseTime }}</small>
          <p class="news-details-footed-list-content">
            {{ detail.next.intro }}
          </p>
          <RouterLink :to="`/journalism/detail/${detail.next.id}`" class="btn no-underline">
            <template v-if="detail.haveNext">
              下一篇<span class="i-home-icons:arrow-right"></span>
            </template>
            <template v-else>
              推荐阅读
            </template>
          </RouterLink>
        </div>
      </div>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.page-journalism-detail {
  margin-top: 48px;

  .news-top-image {
    transform: translate(-45%, -50%);
  }

  .page-journalism-detail__container {
    @apply w-1360px min-h-1000px <2xl:w-1200px;

    .news-header {
      width: 100%;
      margin: 0 auto;
      margin-top: 96px;
    }

    h3 {
      margin: 0;
      color: #2d2d2d;
      font-weight: 500;
      font-size: 40px;
      line-height: 60px;
      letter-spacing: 0;
      text-align: center;
      vertical-align: top;
    }

    p {
      color: rgba(154, 158, 166, 1);
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0;
      text-align: center;
      vertical-align: top;
    }

    .crumbs-content {
      color: rgba(154, 158, 166, 1);
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0;
      text-align: left;
      vertical-align: top;
      list-style: none;
    }

    .crumbs-list {
      color: rgba(154, 158, 166, 1);
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 0;
      text-align: left;
      vertical-align: top;
      list-style: none;

      .crumbs-list-title {
        &:hover {
          color: rgba(17, 128, 255, 1);
        }
      }
    }

    .news-details-content {
      box-sizing: border-box;
      width: 960px;
      margin-top: 96px;
      // min-height: 1000px;
      padding-bottom: 80px;
      color: #2d2d2d;
      white-space: wrap;
      overflow-wrap: break-word;
      @apply mx-auto;

      &:deep() {
        h1,
        h2 {
          margin-top: 48px;
        }

        p,
        section {
          margin-top: 1em;
          margin-bottom: 1em;
          font-weight: 400;
          font-size: 16px;
          line-height: 24px;
        }

        img {
          max-width: 100%;
        }
      }
    }
  }

  .news-details-footed {
    width: 100%;
    height: 400px;
    background: rgba(45, 45, 45, 0.04);
  }

  .news-details-footed-list {
    @apply flex justify-around w-1360px mx-auto my-0 <2xl:w-1200px;

    .news-details-footed-list__item:first-child {
      margin-right: 48px;
    }

    .news-details-footed-list__item {
      width: 656px;
      height: 304px;
      margin: 48px 0;
      padding: 32px 48px;
      background: rgba(255, 255, 255, 1);
      border-radius: 0 0, 4px, 4px;
      @apply flex flex-col box-border;

      h3 {
        display: -webkit-box;
        margin: 0;
        overflow: hidden;
        color: #2d2d2d;
        font-weight: 500;
        font-size: 24px;
        line-height: 36px;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        @apply flex-shrink-0;

        a {
          display: block;
          color: #2d2d2d;

          &:hover {
            color: rgba(17, 128, 255, 1);
            cursor: pointer;
          }
        }
      }

      small {
        display: block;
        margin-top: 8px;
        overflow: hidden;
        color: #adb1ba;
        font-weight: 400;
        font-size: 14px;
        text-align: right;
        @apply flex-shrink-0;
      }

      .news-details-footed-list-content {
        margin: 0;
        margin-top: 16px;
        overflow: hidden;
        color: #9a9ea6;
        font-weight: 400;
        font-size: 16px;
        line-height: 24px;
        @apply flex-grow;
      }

      .btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 560px;
        height: 40px;
        margin-top: 16px;
        color: rgba(255, 255, 255, 1);
        font-weight: 400;
        font-size: 16px;
        line-height: 40px;
        letter-spacing: 0;
        text-align: center;
        vertical-align: middle;
        // margin-bottom: 32px;
        background: rgba(17, 128, 255, 1);
        border: none;
        border-radius: 2px;

        @apply flex-shrink-0;

        img {
          vertical-align: middle;
        }
      }
    }
  }
}
</style>
